import React from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDeleteConfirmState } from '@/components/hooks';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Edit2, Trash2, StopCircle, FileText, Wrench, Rocket, Activity, Archive, RotateCcw, ShieldAlert, Layers, Zap, Sparkles, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SignalDetectionPanel from '@/components/issues/SignalDetectionPanel';
import DensityDetectionPanel from '@/components/issues/DensityDetectionPanel';
import RockResolutionPanel from '@/components/issues/RockResolutionPanel';
import RockTrackIndicator from '@/components/issues/RockTrackIndicator';
import BalloonTrackIndicator from '@/components/issues/BalloonTrackIndicator';
import BalloonResolutionPanel from '@/components/issues/BalloonResolutionPanel';
import PolicyDialog from '@/components/issues/PolicyDialog';
import CapabilityDialog from '@/components/issues/CapabilityDialog';
import { IssueEnrichButton, IssueEnrichmentDisplay } from '@/components/council/IssueEnrichmentPanel';
import IssueAppealPanel from '@/components/council/IssueAppealPanel';
import { Scale } from 'lucide-react';

export default function IssueCard({ issue, officers, missions, objectives, policies, capabilities, activities, onEdit, onDelete, onArchive, onUnarchive, isSeniorOfficer }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const deleteConfirm = useDeleteConfirmState({ confirmTimeout: 3000 });
  const [showGate1Dialog, setShowGate1Dialog] = React.useState(false);
  const [showGate2Dialog, setShowGate2Dialog] = React.useState(false);
  const [showRockPanel, setShowRockPanel] = React.useState(false);
  const [showBalloonPanel, setShowBalloonPanel] = React.useState(false);
  const [showPolicyDialog, setShowPolicyDialog] = React.useState(false);
  const [showCapabilityDialog, setShowCapabilityDialog] = React.useState(false);
  const [showEnrichmentDialog, setShowEnrichmentDialog] = React.useState(false);
  const [localEnrichment, setLocalEnrichment] = React.useState(issue.ai_enrichment);
  const [showAppealDialog, setShowAppealDialog] = React.useState(false);

  const updateIssueMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Issue.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      queryClient.invalidateQueries({ queryKey: ['reportIssues'] });
    }
  });

  const reporter = officers.find((o) => o.user_email === issue.reported_by);
  const decisionMaker = officers.find((o) => o.user_email === issue.decision_made_by);
  const linkedMission = missions?.find((m) => m.id === issue.linked_mission_id);
  const linkedObjective = objectives.find((o) => o.id === issue.linked_objective_id);
  const linkedActivity = activities?.find((a) => a.id === issue.linked_activity_id);
  const linkedPolicy = policies.find((p) => p.id === issue.linked_policy_id);
  const linkedCapability = capabilities.find((c) => c.id === issue.linked_capability_id);

  // Get source context (mission, objective, activity) - always show if any linked entities exist
  const getSourceContext = () => {
    // First try to use direct linked entities (works for both manual and non-manual)
    let activity = linkedActivity;
    let objective = linkedObjective;
    let mission = linkedMission;

    // For activity-sourced issues, derive upward from activity if not directly linked
    if (!objective && activity) {
      objective = objectives.find((o) => o.id === activity.objective_id);
    }
    if (!mission && objective) {
      mission = missions?.find((m) => m.id === objective.mission_id);
    }

    // For non-manual sources, also check source_id if linked entities are missing
    if (issue.source !== 'manual' && !activity && !objective && !mission) {
      if (issue.source === 'activity' || issue.source === 'progress_report' || issue.source === 'task') {
        activity = activities?.find((a) => a.id === issue.source_id || a.id === issue.activity_id);
        if (activity) {
          objective = objectives.find((o) => o.id === activity.objective_id);
          if (objective) {
            mission = missions?.find((m) => m.id === objective.mission_id);
          }
        }
      } else if (issue.source === 'objective') {
        objective = objectives.find((o) => o.id === issue.source_id);
        if (objective) {
          mission = missions?.find((m) => m.id === objective.mission_id);
        }
      } else if (issue.source === 'mission') {
        mission = missions?.find((m) => m.id === issue.source_id);
      }
    }

    const parts = [];
    if (mission) parts.push({ code: mission.mission_code, name: mission.title });
    if (objective) parts.push({ code: objective.objective_code, name: `${objective.statement?.substring(0, 40)}...` });
    if (activity) parts.push({ code: activity.activity_code, name: activity.title });
    return parts.length > 0 ? parts : null;
  };

  const sourceContext = getSourceContext();

  const calculateLevel = (xp) => Math.floor((xp || 0) / 1000) + 1;

  const categoryColors = {
    red_line_violation: 'bg-red-500/20 text-red-400 border-red-500/30',
    execution_headwind: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    capability_gap: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    process_friction: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    other: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
  };

  const severityColors = {
    low: 'bg-slate-500/20 text-slate-400',
    medium: 'bg-yellow-500/20 text-yellow-400',
    high: 'bg-orange-500/20 text-orange-400',
    critical: 'bg-red-500/20 text-red-400'
  };

  const statusColors = {
    open: 'bg-red-500/20 text-red-400',
    under_review: 'bg-amber-500/20 text-amber-400',
    decision_made: 'bg-blue-500/20 text-blue-400',
    resolved: 'bg-emerald-500/20 text-emerald-400',
    archived: 'bg-slate-500/20 text-slate-400',
    rejected: 'bg-red-600/20 text-red-300'
  };

  const gate1Colors = {
    pending: 'bg-slate-500/20 text-slate-400',
    noise: 'bg-gray-500/20 text-gray-400',
    signal: 'bg-amber-500/20 text-amber-400'
  };
  const gate2Colors = {
    pending: 'bg-slate-500/20 text-slate-400',
    balloon: 'bg-sky-500/20 text-sky-400',
    rock: 'bg-stone-600/30 text-stone-300'
  };
  const frequencyColors = {
    first_occurrence: 'bg-slate-500/20 text-slate-400',
    recurring: 'bg-amber-500/20 text-amber-400',
    escalating: 'bg-red-500/20 text-red-400'
  };
  const frequencyLabels = { first_occurrence: 'First', recurring: 'Recurring', escalating: 'Escalating' };

  const resolutionOwnerProfile = officers.find((o) => o.user_email === issue.resolution_owner);

  const getDecisionIcon = (decision) => {
    switch (decision) {
      case 'cease_activity':return <StopCircle className="w-4 h-4" />;
      case 'design_policy':return <FileText className="w-4 h-4" />;
      case 'contract_capability':return <Wrench className="w-4 h-4" />;
      case 'develop_capability':return <Rocket className="w-4 h-4" />;
      default:return null;
    }
  };

  const leftBorderClass = issue.classification_gate2 === 'rock' ?
  'border-l-4 border-l-red-500/60' :
  issue.classification_gate2 === 'balloon' ?
  'border-l-4 border-l-sky-500/60' :
  '';

  return (
    <Card className={`glass-panel border-slate-800 relative ${leftBorderClass}`}>
      {/* Source Earmark */}
      <div className={`absolute top-0 right-0 px-6 py-3 text-base font-bold text-white rounded-bl-2xl shadow-lg ${
      issue.source === 'mission' ? 'bg-amber-600' :
      issue.source === 'objective' ? 'bg-indigo-500' :
      issue.source === 'activity' ? 'bg-red-500' :
      issue.source === 'task' ? 'bg-purple-500' :
      issue.source === 'progress_report' ? 'bg-orange-500' :
      issue.source === 'scan' ? 'bg-emerald-500' :
      'bg-blue-500'}`
      }>
        {issue.source === 'mission' ? 'Mission' :
        issue.source === 'objective' ? 'Objective' :
        issue.source === 'activity' ? 'Activity' :
        issue.source === 'task' ? 'Task' :
        issue.source === 'progress_report' ? 'Progress Report' :
        issue.source === 'scan' ? 'Scan' :
        'Manual'}
      </div>

      <CardHeader>
        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle className="w-5 h-5 text-red-400" />
          <CardTitle className="text-lg">{issue.title}</CardTitle>
        </div>
        <p className="text-xs text-slate-500 font-mono mb-2">{issue.id}</p>
        <div className="flex flex-wrap gap-2">
          <Badge className={categoryColors[issue.category]}>
            {issue.category?.replace('_', ' ')}
          </Badge>
          {issue.severity &&
          <Badge className={severityColors[issue.severity]}>
              {issue.severity}
            </Badge>
          }
          <Badge className={statusColors[issue.status]}>
            {issue.status?.replace('_', ' ')}
          </Badge>
          {issue.frequency_signal &&
          <Badge className={frequencyColors[issue.frequency_signal]}>
              {frequencyLabels[issue.frequency_signal] || issue.frequency_signal}
            </Badge>
          }
          {issue.classification_gate1 && issue.classification_gate1 !== 'pending' &&
          <Badge className={gate1Colors[issue.classification_gate1]}>
              G1: {issue.classification_gate1 === 'signal' ? 'Signal' : 'Noise'}
            </Badge>
          }
          {issue.classification_gate1 === 'signal' && issue.classification_gate2 && issue.classification_gate2 !== 'pending' &&
          <Badge className="bg-stone-600/30 text-stone-700 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">
              {issue.classification_gate2 === 'balloon' ? '🎈 Balloon' : '🪨 Rock'}
            </Badge>
          }
        </div>
        <div className="flex gap-1 mt-3 flex-wrap">
          {/* AI Enrichment Button */}
          {!issue.progress_report_id && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
              onClick={() => setShowEnrichmentDialog(true)}
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {localEnrichment ? 'View AI Analysis' : 'AI Enrich'}
            </Button>
          )}
          {/* Appeal Button - for rejected issues or decisions */}
          {(issue.status === 'rejected' || (issue.status === 'decision_made' && issue.appeal_status !== 'appeal_accepted')) && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
              onClick={() => setShowAppealDialog(true)}
            >
              <Scale className="w-3.5 h-3.5 mr-1" />
              {issue.appeal_status === 'appealed' ? 'Appeal Pending' : 
               issue.appeal_status === 'appeal_denied' ? 'Appeal Denied' : 'Appeal'}
            </Button>
          )}
          {isSeniorOfficer && !issue.progress_report_id && (!issue.classification_gate1 || issue.classification_gate1 === 'pending') &&
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            onClick={() => setShowGate1Dialog(true)}>

              <ShieldAlert className="w-3.5 h-3.5 mr-1" />
              Run Gate 1
            </Button>
          }
          {isSeniorOfficer && !issue.progress_report_id && issue.classification_gate1 && issue.classification_gate1 !== 'pending' &&
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-slate-400 hover:text-slate-200"
            onClick={() => setShowGate1Dialog(true)}>

              <ShieldAlert className="w-3.5 h-3.5 mr-1" />
              Review Gate 1
            </Button>
          }
          {isSeniorOfficer && !issue.progress_report_id && issue.classification_gate1 === 'signal' && (!issue.classification_gate2 || issue.classification_gate2 === 'pending') &&
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
            onClick={() => setShowGate2Dialog(true)}>

              <Layers className="w-3.5 h-3.5 mr-1" />
              Run Gate 2
            </Button>
          }
          {isSeniorOfficer && !issue.progress_report_id && issue.classification_gate2 && issue.classification_gate2 !== 'pending' &&
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-slate-400 hover:text-slate-200"
            onClick={() => setShowGate2Dialog(true)}>

              <Layers className="w-3.5 h-3.5 mr-1" />
              Review Gate 2
            </Button>
          }
          {onEdit &&
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(issue)}>
              <Edit2 className="w-4 h-4" />
            </Button>
          }
          {(issue.status === 'resolved' || issue.status === 'archived') && onUnarchive ?
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => onUnarchive.mutate(issue.id)}
            title="Unarchive">
              <RotateCcw className="w-4 h-4" />
            </Button> :
          onArchive &&
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => onArchive.mutate(issue.id)}
            title="Archive">
              <Archive className="w-4 h-4" />
            </Button>
          }
          {onDelete &&
          <div className="relative">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 hover:text-red-400"
              onClick={() => deleteConfirm.checkAndConfirm(issue.id, () => onDelete.mutate(issue.id))}>

              <Trash2 className="w-4 h-4" />
            </Button>
            {deleteConfirm.isPendingConfirm(issue.id) &&
            <span className="absolute right-0 -bottom-8 text-xs text-red-400 bg-slate-900 px-2 py-1 rounded border border-red-500/30 whitespace-nowrap z-10">
                Click again to confirm
              </span>
            }
          </div>
          }
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {issue.description &&
        <p className="text-slate-700 text-sm">{issue.description}</p>
        }

        {sourceContext &&
        <p className="text-xs text-slate-400">
            <span className="text-slate-500 font-medium">Context: </span>
            {sourceContext.map((item, idx) =>
          <span key={idx}>
                {idx > 0 && ' → '}
                <span
              className="font-bold text-blue-400 hover:text-blue-300 cursor-pointer underline"
              onClick={() => navigate(createPageUrl('civilizationmap') + `?search=${encodeURIComponent(item.code)}`)}>

                  [{item.code}]
                </span> {item.name}
              </span>
          )}
          </p>
        }

        {/* Rejection Info */}
        {issue.status === 'rejected' && (
          <div className="p-3 bg-red-950/20 border border-red-500/20 rounded-lg space-y-1">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="text-sm font-semibold text-red-300">Rejected</span>
              {issue.rejection_reason && (
                <Badge className="bg-red-500/20 text-red-300 text-[10px]">
                  {issue.rejection_reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                </Badge>
              )}
            </div>
            {issue.rejection_notes && <p className="text-xs text-red-300/70 italic">{issue.rejection_notes}</p>}
            {issue.rejected_by && (
              <p className="text-[10px] text-slate-500">
                By {officers.find(o => o.user_email === issue.rejected_by)?.callsign || issue.rejected_by}
                {issue.rejected_at && ` on ${format(new Date(issue.rejected_at), 'MMM d, yyyy')}`}
              </p>
            )}
          </div>
        )}

        {/* Appeal Status */}
        {issue.appeal_status && issue.appeal_status !== 'none' && (
          <div className={`p-3 rounded-lg border space-y-1 ${
            issue.appeal_status === 'appealed' ? 'bg-amber-950/20 border-amber-500/20' :
            issue.appeal_status === 'appeal_accepted' ? 'bg-emerald-950/20 border-emerald-500/20' :
            'bg-slate-800/30 border-slate-700'
          }`}>
            <div className="flex items-center gap-2">
              <Scale className="w-4 h-4 text-amber-400" />
              <span className={`text-sm font-semibold ${
                issue.appeal_status === 'appealed' ? 'text-amber-300' :
                issue.appeal_status === 'appeal_accepted' ? 'text-emerald-300' : 'text-slate-400'
              }`}>
                Appeal {issue.appeal_status === 'appealed' ? 'Pending' : issue.appeal_status === 'appeal_accepted' ? 'Accepted' : 'Denied'}
              </span>
            </div>
            {issue.appeal_reason && <p className="text-xs text-slate-300">{issue.appeal_reason}</p>}
          </div>
        )}

        {issue.observed_impact &&
        <div className="text-sm">
            <span className="text-slate-500 font-medium">Impact: </span>
            <span className="text-slate-700">{issue.observed_impact}</span>
          </div>
        }

        {issue.resolution_owner && resolutionOwnerProfile &&
        <div className="text-sm">
            <span className="text-slate-500 font-medium">Resolution Owner: </span>
            <span className="text-cyan-400">{resolutionOwnerProfile.callsign}</span>
          </div>
        }

        {issue.classification_gate2 === 'rock' &&
        <RockTrackIndicator issue={issue} onClick={() => setShowRockPanel(true)} />
        }
        {issue.classification_gate2 === 'balloon' &&
        <BalloonTrackIndicator issue={issue} onClick={() => setShowBalloonPanel(true)} />
        }

        <div className="grid grid-cols-2 gap-3 text-sm">
          {issue.reported_by &&
          <div>
              <span className="text-slate-500">Reported by:</span>
              <span className="text-slate-600 ml-2">
                {reporter ? `${reporter.callsign}, ${reporter.rank_title}, L${calculateLevel(reporter.total_xp)}` : issue.reported_by}
              </span>
            </div>
          }


          {issue.reported_timestamps &&
          <div className="col-span-2">
              <span className="text-slate-500">Reported:</span>
              <span className="text-slate-700 ml-2">
                {issue.reported_timestamps}
              </span>
            </div>
          }
          {issue.reported_at && !issue.reported_timestamps &&
          <div>
              <span className="text-slate-500">Reported:</span>
              <span className="text-slate-700 ml-2">
                {format(new Date(issue.reported_at), 'MMM d, yyyy HH:mm')}
              </span>
            </div>
          }
          {issue.created_date && !issue.reported_at &&
          <div>
              <span className="text-slate-500">Created:</span>
              <span className="ml-2 text-slate-300">
                {format(new Date(issue.created_date), 'MMM d, yyyy HH:mm')}
              </span>
            </div>
          }
        </div>

        {/* Approach Decisions */}
        {issue.classification_gate2 === 'rock' && (issue.rock_track2_pathway || issue.decision) &&
        <div className="space-y-2">
            {issue.rock_track2_pathway &&
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Rocket className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm font-semibold text-emerald-400">
                    T2 Capability Approach: {issue.rock_track2_pathway.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </div>
              </div>
            }
            {issue.decision &&
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">
                    T3 Operational Approach: {issue.decision.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                </div>
                {decisionMaker &&
                <p className="text-xs text-slate-400 mt-1">
                    By {decisionMaker.callsign} on {format(new Date(issue.decision_made_at), 'MMM d, yyyy')}
                  </p>
                }
                {issue.decision_notes &&
                <p className="text-xs text-slate-300 mt-1">{issue.decision_notes}</p>
                }
              </div>
            }
          </div>
        }

        {issue.classification_gate2 === 'balloon' && issue.decision &&
        <div className="p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-sky-400" />
              <span className="text-sm font-semibold text-sky-400">
                Approach: {issue.decision.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </span>
            </div>
            {decisionMaker &&
          <p className="text-xs text-slate-400 mt-1">
                By {decisionMaker.callsign} on {format(new Date(issue.decision_made_at), 'MMM d, yyyy')}
              </p>
          }
            {issue.decision_notes &&
          <p className="text-xs text-slate-300 mt-1">{issue.decision_notes}</p>
          }
          </div>
        }

        {/* Legacy decision display for non-classified issues */}
        {!issue.classification_gate2 && issue.decision &&
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              {getDecisionIcon(issue.decision)}
              <span className="text-sm font-semibold text-indigo-400">
                Decision: {issue.decision.replace(/_/g, ' ').toUpperCase()}
              </span>
            </div>
            {decisionMaker &&
          <p className="text-xs text-slate-400 mb-1">
                By {decisionMaker.callsign} on {format(new Date(issue.decision_made_at), 'MMM d, yyyy')}
              </p>
          }
            {issue.decision_notes &&
          <p className="text-xs text-slate-300">{issue.decision_notes}</p>
          }
          </div>
        }

        {/* Tags Section - always show if any tags exist */}
        {(issue.tagged_officers?.length > 0 || issue.tagged_artifacts?.length > 0 || issue.tagged_rituals?.length > 0 || issue.tagged_policies?.length > 0 || issue.tagged_capabilities?.length > 0) &&
        <div className="space-y-2">
            <p className="text-xs text-slate-500 font-semibold">Tagged Elements:</p>
            <div className="flex flex-wrap gap-1">
              {issue.tagged_officers?.filter((tag) => !(issue.removed_tags || []).some((rt) => rt.tag_type === 'tagged_officers' && rt.tag_value === tag)).map((email, idx) =>
            <Badge key={idx} variant="outline" className="text-xs border-indigo-500/30 text-indigo-400">
                  Officer: {officers.find((o) => o.user_email === email)?.callsign || email}
                </Badge>
            )}
              {issue.tagged_artifacts?.filter((tag) => !(issue.removed_tags || []).some((rt) => rt.tag_type === 'tagged_artifacts' && rt.tag_value === tag)).map((name, idx) =>
            <Badge key={idx} variant="outline" className="text-xs border-cyan-500/30 text-cyan-400">
                  Artifact: {name}
                </Badge>
            )}
              {issue.tagged_rituals?.filter((tag) => !(issue.removed_tags || []).some((rt) => rt.tag_type === 'tagged_rituals' && rt.tag_value === tag)).map((name, idx) =>
            <Badge key={idx} variant="outline" className="text-xs border-purple-500/30 text-purple-400">
                  Ritual: {name}
                </Badge>
            )}
              {issue.tagged_policies?.filter((tag) => !(issue.removed_tags || []).some((rt) => rt.tag_type === 'tagged_policies' && rt.tag_value === tag)).map((name, idx) =>
            <Badge key={idx} variant="outline" className="text-xs border-amber-500/30 text-amber-400">
                  Policy: {name}
                </Badge>
            )}
              {issue.tagged_capabilities?.filter((tag) => !(issue.removed_tags || []).some((rt) => rt.tag_type === 'tagged_capabilities' && rt.tag_value === tag)).map((name, idx) =>
            <Badge key={idx} variant="outline" className="text-xs border-emerald-500/30 text-emerald-400">
                  Capability: {name}
                </Badge>
            )}
            </div>
          </div>
        }

        {(linkedPolicy || linkedCapability) &&
        <div className="space-y-2">
            <p className="text-xs text-slate-500 font-semibold">Linked Items:</p>
            {linkedPolicy &&
          <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30">
                Policy: {linkedPolicy.title}
              </Badge>
          }
            {linkedCapability &&
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                Capability: {linkedCapability.name}
              </Badge>
          }
          </div>
        }
      </CardContent>

      {/* Gate 1 Assessment Dialog */}
      <Dialog open={showGate1Dialog} onOpenChange={setShowGate1Dialog}>
        <DialogContent data-testid="issues-issuecard-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Gate 1 Assessment: {issue.title}</DialogTitle>
          </DialogHeader>
          <SignalDetectionPanel
            issue={issue}
            onComplete={() => setShowGate1Dialog(false)} />

        </DialogContent>
      </Dialog>

      {/* Gate 2 Assessment Dialog */}
      <Dialog open={showGate2Dialog} onOpenChange={setShowGate2Dialog}>
        <DialogContent data-testid="issues-issuecard-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Gate 2 Assessment: {issue.title}</DialogTitle>
          </DialogHeader>
          <DensityDetectionPanel
            issue={issue}
            onComplete={() => setShowGate2Dialog(false)} />

        </DialogContent>
      </Dialog>

      {/* Rock 3-Track Resolution Dialog */}
      <Dialog open={showRockPanel} onOpenChange={setShowRockPanel}>
        <DialogContent data-testid="issues-issuecard-dialog-2" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Rock Resolution: {issue.title}</DialogTitle>
          </DialogHeader>
          <RockResolutionPanel
            issue={issue}
            officers={officers}
            policies={policies}
            capabilities={capabilities}
            onCreatePolicy={() => setShowPolicyDialog(true)}
            onCreateCapability={() => setShowCapabilityDialog(true)}
            onComplete={() => setShowRockPanel(false)} />

        </DialogContent>
      </Dialog>

      {/* Balloon Resolution Dialog */}
      <Dialog open={showBalloonPanel} onOpenChange={setShowBalloonPanel}>
        <DialogContent data-testid="issues-issuecard-dialog-3" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">Balloon Resolution: {issue.title}</DialogTitle>
          </DialogHeader>
          <BalloonResolutionPanel
            issue={issue}
            officers={officers}
            onComplete={() => setShowBalloonPanel(false)}
          />
        </DialogContent>
      </Dialog>

      <PolicyDialog
        open={showPolicyDialog}
        onClose={() => setShowPolicyDialog(false)}
        policy={{ linked_issue_ids: [issue.id], interim_for_capability_development: true }}
        officers={officers}
        issues={[issue]}
        capabilities={capabilities}
      />

      <CapabilityDialog
        open={showCapabilityDialog}
        onClose={() => setShowCapabilityDialog(false)}
        capability={{ linked_issue_ids: [issue.id] }}
        officers={officers}
        issues={[issue]}
        policies={policies}
        objectives={objectives}
      />

      {/* AI Enrichment Dialog */}
      <Dialog open={showEnrichmentDialog} onOpenChange={setShowEnrichmentDialog}>
        <DialogContent data-testid="issues-issuecard-dialog-4" className="bg-slate-900 border-slate-800 text-slate-100 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              AI Enrichment: {issue.title}
            </DialogTitle>
          </DialogHeader>
          {localEnrichment ? (
            <IssueEnrichmentDisplay enrichment={localEnrichment} />
          ) : (
            <div className="flex flex-col items-center gap-4 py-6">
              <p className="text-sm text-slate-400 text-center">This issue hasn't been AI-enriched yet.</p>
              <IssueEnrichButton
                issue={issue}
                onEnriched={(id, enrichment) => {
                  setLocalEnrichment(enrichment);
                  queryClient.invalidateQueries({ queryKey: ['issues'] });
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Appeal Dialog */}
      <IssueAppealPanel
        issue={issue}
        officers={officers}
        open={showAppealDialog}
        onClose={() => setShowAppealDialog(false)}
      />

    </Card>);

}